import { useCallback, useRef } from 'react';

interface FieldInteraction {
  fieldId: string;
  action: 'focus' | 'blur' | 'change';
  timestamp: string;
  value?: any;
}

interface UseFieldTrackingOptions {
  formId: string;
  apiUrl?: string;
  enabled?: boolean;
}

export function useFieldTracking({
  formId,
  apiUrl,
  enabled = true,
}: UseFieldTrackingOptions) {
  const interactionsRef = useRef<FieldInteraction[]>([]);
  const sentRef = useRef(false);
  const sessionIdRef = useRef<string | null>(null);
  const fieldFocusTimeRef = useRef<Record<string, number>>({});

  const getApiUrl = useCallback(() => {
    if (apiUrl) return apiUrl;
    
    if (typeof window !== 'undefined' && (window as any).HEXAFORMS_API_URL) {
      return (window as any).HEXAFORMS_API_URL;
    }
    
    return 'https://80usneael9.execute-api.eu-west-1.amazonaws.com/dev';
  }, [apiUrl]);

  // Generate or retrieve persistent session ID
  const getOrCreateSessionId = useCallback(() => {
    if (!sessionIdRef.current) {
      // Try to get from sessionStorage first (persists across page reloads)
      if (typeof window !== 'undefined' && window.sessionStorage) {
        const stored = sessionStorage.getItem(`hexaforms_session_${formId}`);
        if (stored) {
          sessionIdRef.current = stored;
          return stored;
        }
      }
      
      // Generate new session ID
      sessionIdRef.current = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Store in sessionStorage
      if (typeof window !== 'undefined' && window.sessionStorage) {
        sessionStorage.setItem(`hexaforms_session_${formId}`, sessionIdRef.current);
      }
    }
    
    return sessionIdRef.current;
  }, [formId]);

  // Send individual interaction to API (fire-and-forget)
  const sendIndividualInteraction = useCallback(async (
    fieldId: string,
    action: 'focus' | 'blur' | 'change',
    timestamp: number,
    value?: any,
    duration?: number
  ) => {
    if (!enabled) return;

    try {
      const finalApiUrl = getApiUrl();
      const sessionId = getOrCreateSessionId();
      
      await fetch(`${finalApiUrl}/analytics/track/interaction/${formId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId,
          fieldId,
          action,
          timestamp,
          value: action === 'change' ? value : undefined,
          duration: action === 'blur' ? duration : undefined,
        }),
      });
    } catch (error) {
      // Silenciar errores de tracking (no crítico, fire-and-forget)
      if (typeof window !== 'undefined' && (window as any).HEXAFORMS_DEBUG) {
        console.debug('[HexaForms] Error sending individual interaction:', error);
      }
    }
  }, [formId, getApiUrl, enabled, getOrCreateSessionId]);

  const trackFieldInteraction = useCallback((
    fieldId: string,
    action: 'focus' | 'blur' | 'change',
    value?: any
  ) => {
    if (!enabled) return;

    const timestamp = Date.now();
    const interaction: FieldInteraction = {
      fieldId,
      action,
      timestamp: new Date().toISOString(),
      value: action === 'change' ? value : undefined,
    };

    // Track focus time for duration calculation
    if (action === 'focus') {
      fieldFocusTimeRef.current[fieldId] = timestamp;
    }

    // Calculate duration for blur events
    let duration: number | undefined;
    if (action === 'blur' && fieldFocusTimeRef.current[fieldId]) {
      duration = timestamp - fieldFocusTimeRef.current[fieldId];
      delete fieldFocusTimeRef.current[fieldId];
    }

    // Send individual interaction immediately (fire-and-forget)
    sendIndividualInteraction(fieldId, action, timestamp, value, duration);

    // Also accumulate for funnel event (backup/summary)
    interactionsRef.current.push(interaction);
  }, [enabled, sendIndividualInteraction]);

  // Track session start (call when form loads)
  const trackSessionStart = useCallback(async () => {
    if (!enabled) return;

    try {
      const finalApiUrl = getApiUrl();
      const sessionId = getOrCreateSessionId();
      const startedAt = new Date().toISOString();
      
      // Store startedAt for later use in completed/abandoned events
      if (interactionsRef.current.length === 0) {
        interactionsRef.current.push({
          fieldId: '__session_start__',
          action: 'focus',
          timestamp: startedAt,
        });
      }
      
      // Send session_started event
      await fetch(`${finalApiUrl}/analytics/track/funnel/${formId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId,
          eventType: 'session_started',
          startedAt,
        }),
      });
    } catch (error) {
      // Silenciar errores de tracking (no crítico, fire-and-forget)
      if (typeof window !== 'undefined' && (window as any).HEXAFORMS_DEBUG) {
        console.debug('[HexaForms] Error sending session start:', error);
      }
    }
  }, [formId, getApiUrl, enabled, getOrCreateSessionId]);

  const sendFunnelData = useCallback(async (
    status: 'completed' | 'abandoned',
    partialData?: Record<string, any>
  ) => {
    if (!enabled || sentRef.current) return;

    sentRef.current = true;

    try {
      const finalApiUrl = getApiUrl();
      // Use persistent session ID (same as individual interactions)
      const sessionId = getOrCreateSessionId();
      
      // Calculate startedAt from first interaction or current time
      const startedAt = interactionsRef.current.length > 0
        ? interactionsRef.current[0].timestamp
        : new Date().toISOString();
      
      // Calculate progress percentage (for abandoned events)
      let progressPercentage: number | undefined;
      if (status === 'abandoned' && partialData) {
        // Estimate progress based on partialData
        const totalFields = Object.keys(partialData).length;
        // This is a rough estimate - actual calculation should be done by usePartialDataCapture
        progressPercentage = totalFields > 0 ? Math.min(100, totalFields * 10) : 0;
      }
      
      const payload = {
        sessionId,
        eventType: status === 'completed' ? 'session_completed' : 'session_abandoned',
        startedAt,
        [status === 'completed' ? 'completedAt' : 'abandonedAt']: new Date().toISOString(),
        abandonedAtField: status === 'abandoned' 
          ? interactionsRef.current[interactionsRef.current.length - 1]?.fieldId 
          : undefined,
        progressPercentage: status === 'abandoned' ? progressPercentage : undefined,
        totalDuration: status === 'abandoned' 
          ? Date.now() - new Date(startedAt).getTime()
          : undefined,
        // Include accumulated interactions as summary (individual ones already sent)
        interactions: interactionsRef.current,
        // ✅ NEW: Include partialData for abandoned events
        ...(status === 'abandoned' && partialData ? { partialData } : {}),
      };
      
      // Send funnel event with keepalive for reliability
      try {
        await fetch(`${finalApiUrl}/analytics/track/funnel/${formId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
          keepalive: status === 'abandoned', // Critical for abandonment events
        });
      } catch (fetchError) {
        // Fallback: use sendBeacon if fetch fails (more reliable in beforeunload)
        if (status === 'abandoned' && typeof navigator !== 'undefined' && navigator.sendBeacon) {
          const blob = new Blob([JSON.stringify(payload)], {
            type: 'application/json',
          });
          navigator.sendBeacon(`${finalApiUrl}/analytics/track/funnel/${formId}`, blob);
        } else {
          throw fetchError;
        }
      }
    } catch (error) {
      if (typeof window !== 'undefined' && (window as any).HEXAFORMS_DEBUG) {
        console.debug('[HexaForms] Error sending funnel data:', error);
      }
    }
  }, [formId, getApiUrl, enabled, getOrCreateSessionId]);

  return {
    trackFieldInteraction,
    trackSessionStart,
    sendFunnelData,
  };
}
